import { authFetch } from '../../../api/csrf';
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Calendar,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  X,
  FileText,
  ChevronLeft,
  AlertCircle,
  Target,
  Archive,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Upload,
  ChevronDown,
  Loader2
} from "lucide-react";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import BulkUploadModal from "../../../components/common/BulkUploadModal";
import { exportToCSV, generateExportFilename, downloadFromBackend } from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import DateRangeFilter from "../../../components/common/DateRangeFilter";

import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { toast } from 'react-hot-toast';
import WarningPopup from "../../../components/WarningPopup";
import { usePerformanceCycles, usePerformanceCycle } from '../../../hooks/usePerformanceCycles';
import { formatDate } from "../../../utils/stringUtils";

const PerformanceCycles = ({ initialMode, onClose, isDrawer, cycleId }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || "");
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get('limit')) || 10);

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterYear, setFilterYear] = useState(searchParams.get('year') || "all");
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || '');
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || '');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCycleId, setEditingCycleId] = useState(cycleId || null);
  const { data: editingCycleData, isLoading: isLoadingEditingCycle } = usePerformanceCycle(editingCycleId);
  const [formErrors, setFormErrors] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'year', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterYear && filterYear !== 'all') params.set('year', filterYear);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      if (page && page !== 1) params.set('page', String(page));
      if (limit && limit !== 10) params.set('limit', String(limit));

      return params;
    }, { replace: true });
  }, [debouncedSearch, filterStatus, filterYear, filterStartDate, filterEndDate, page, limit, setSearchParams]);

  const baseExportFilters = useMemo(() => ({
    status: filterStatus !== "all" ? filterStatus : undefined,
    year: filterYear !== "all" ? filterYear : undefined,
    q: debouncedSearch || undefined,
  }), [filterStatus, filterYear, debouncedSearch]);

  const requestBackendExport = React.useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: "performance-cycles",
        scope,
        format,
        filters,
        page: overridePage ?? page,
        limit: overrideLimit ?? 10,
        sortBy: "createdAt",
        sortOrder: "desc",
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Export failed");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `performance-cycles-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page]
  );
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [cycleToDelete, setCycleToDelete] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => {
    if (initialMode === 'create') {
      setShowCreateModal(true);
    } else if (initialMode === 'edit' && cycleId) {
      setEditingCycleId(cycleId);
      setShowCreateModal(true);
    }
  }, [initialMode, cycleId]);

  // Sync edit parameter from URL query params
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      setEditingCycleId(editId);
      setShowCreateModal(true);
    }
  }, [searchParams]);

  // Prefill form in edit mode
  useEffect(() => {
    if (editingCycleId && editingCycleData) {
      setNewCycle({
        name: editingCycleData.cycleName || editingCycleData.name || "",
        description: editingCycleData.description || "",
        startDate: editingCycleData.startDate ? editingCycleData.startDate.split("T")[0] : "",
        endDate: editingCycleData.endDate ? editingCycleData.endDate.split("T")[0] : "",
        status: editingCycleData.status || "draft",
      });
      setTouched({});
      setFormErrors({});
    }
  }, [editingCycleId, editingCycleData]);

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingCycleId(null);
    setNewCycle({
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      status: "draft",
    });
    setTouched({});
    setFormErrors({});
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.delete('edit');
      return params;
    }, { replace: true });
    onClose?.();
  };

  // React Query hooks
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const cycleFilters = useMemo(
    () => ({
      status: filterStatus !== "all" ? filterStatus : undefined,
      year: filterYear !== "all" ? filterYear : undefined,
      createdAtStart: filterStartDate || undefined,
      createdAtEnd: filterEndDate || undefined,
      q: debouncedSearch || undefined,
      page,
      limit,
    }),
    [filterStatus, filterYear, filterStartDate, filterEndDate, debouncedSearch, page, limit]
  );

  const {
    data: cyclesResponse,
    isLoading,
    isError,
    error,
    refetch,
    createCycle,
    deleteCycle,
    launchCycle,
    updateCycle,
  } = usePerformanceCycles(cycleFilters);

  // Transform API data to match frontend format
  let cyclesData = [];
  let paginationData = null;

  if (cyclesResponse?.data?.cycles) {
    cyclesData = cyclesResponse.data.cycles;
    paginationData = cyclesResponse.data.pagination;
  } else if (cyclesResponse?.cycles && Array.isArray(cyclesResponse.cycles)) {
    cyclesData = cyclesResponse.cycles;
  } else if (cyclesResponse?.data && Array.isArray(cyclesResponse.data)) {
    cyclesData = cyclesResponse.data;
  } else if (Array.isArray(cyclesResponse)) {
    cyclesData = cyclesResponse;
  }

  const dataToUse = cyclesData;

  const transformedCycles = dataToUse.map((cycle) => {
    return {
      id: cycle.id,
      name: cycle.cycleName || cycle.name,
      description: cycle.description || "",
      startDateRaw: cycle.startDate,
      endDateRaw: cycle.endDate,
      period: (() => {
        if (!cycle.startDate || !cycle.endDate) return 'N/A';
        try {
          const startFormatted = formatDate(cycle.startDate, { includeTime: false });
          const endFormatted = formatDate(cycle.endDate, { includeTime: false });
          const startParts = startFormatted.split(' ');
          const endParts = endFormatted.split(' ');
          if (startParts.length >= 3 && endParts.length >= 3) {
            return `${startParts[1]} ${startParts[2]} - ${endParts[1]} ${endParts[2]}`;
          }
          return `${startFormatted} - ${endFormatted}`;
        } catch (error) {
          return 'N/A';
        }
      })(),
      year: new Date(cycle.startDate).getFullYear(),
      status: cycle.status,
      population: cycle.metrics?.totalEmployees || cycle.totalEmployees || 0,
      completion:
        cycle.metrics?.completionPercentage || cycle.completionPercentage || 0,
      dueDate: cycle.endDate,
      manager: cycle.manager || "System",
      stages: cycle.stages?.map((stage) => stage.name) || [
        "Self Review",
        "Manager Review",
        "Calibration",
        "Final Rating",
      ],
      createdAt: cycle.createdAt,
      launchedAt: cycle.launchedAt,
      frozenAt: cycle.frozenAt,
      closedAt: cycle.closedAt,
      archived: cycle.isArchived || false,
      averageRating: cycle.metrics?.averageRating || cycle.averageRating || 0,
      duration: cycle.duration,
      daysRemaining: cycle.daysRemaining,
      isActive: cycle.isActive,
      isOverdue: cycle.isOverdue,
    };
  });

  const [newCycle, setNewCycle] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "draft",
  });

  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    let error = undefined;
    switch (name) {
      case 'name':
        if (!value?.trim()) return "Please enter a valid name";
        if (value.length > 100) return "Name must be ≤ 100 characters";
        if (!/^[a-zA-Z0-9\s,]+$/.test(value)) return "Name can only contain alphanumeric characters, spaces, and commas";
        if (value.trim().length < 3) return "Please enter a valid name (min 3 chars)";
        break;
      case 'description':
        if (!value?.trim()) return "Please enter a valid description";
        if (value.length > 500) return "Description must be ≤ 500 characters";
        if (!/^[a-zA-Z0-9\s,]+$/.test(value)) return "Description can only contain alphanumeric characters, spaces, and commas";
        if (value.trim().length < 5) return "Please enter a valid description (min 5 chars)";
        break;
      case 'startDate':
        if (!value) return "Start date is required";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!editingCycleId && new Date(value) < today) return "Start date cannot be in the past";
        break;
      case 'endDate':
        if (!value) return "End date is required";
        if (newCycle.startDate && new Date(value) < new Date(newCycle.startDate)) return "End date must be after start date";
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "launched":
        return "bg-blue-100 text-blue-800";
      case "calibrating":
        return "bg-yellow-100 text-yellow-800";
      case "frozen":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "draft":
        return <Clock className="h-4 w-4" />;
      case "launched":
        return <Target className="h-4 w-4" />;
      case "calibrating":
        return <BarChart3 className="h-4 w-4" />;
      case "frozen":
        return <CheckCircle className="h-4 w-4" />;
      case "closed":
        return <Archive className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getCyclePeriodDisplay = (cycle) => {
    if (!cycle) return "N/A";
    const start = cycle.startDateRaw || cycle.startDate || cycle.start_date;
    const end = cycle.endDateRaw || cycle.endDate || cycle.end_date;
    try {
      if (start) {
        const startFormatted = formatDate(start, { includeTime: false });
        if (end) {
          const endFormatted = formatDate(end, { includeTime: false });
          return `${startFormatted} - ${endFormatted}`;
        }
          return startFormatted;
      }
      if (cycle.period && cycle.period !== "N/A") {
        return cycle.period;
      }
    } catch (err) {
    }
    return "N/A";
  };

  const scheduleDefaultStages = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return [];
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const totalMs = end.getTime() - start.getTime();
    if (totalMs <= 0) return [];
    const labels = [
      "goal_setting",
      "self_review",
      "manager_review",
      "consolidation",
      "calibration",
      "finalization",
    ];
    const step = Math.floor(totalMs / (labels.length + 1));
    const endMinus1 = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    return labels.map((name, index) => {
      let due = new Date(start.getTime() + step * (index + 1));
      const stageStart = new Date(start.getTime() + step * index);
      if (due > endMinus1) due = endMinus1;
      if (stageStart >= due) {
        due = new Date(stageStart.getTime() + 24 * 60 * 60 * 1000);
        if (due > endMinus1) due = endMinus1;
      }
      return {
        name,
        order: index + 1,
        isRequired: true,
        isCompleted: false,
        startDate: stageStart,
        dueDate: due,
        description: `${name.replace(/_/g, ' ')} phase`,
      };
    });
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!newCycle.name?.trim()) {
      errors.name = "Please enter a valid name";
    } else if (newCycle.name.length > 100) {
      errors.name = "Name must be ≤ 100 characters";
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(newCycle.name)) {
      errors.name = "Name can only contain alphanumeric characters, spaces, and commas";
    } else if (newCycle.name.trim().length < 3) {
      errors.name = "Please enter a valid name (min 3 chars)";
    }
    if (!newCycle.description?.trim()) {
      errors.description = "Please enter a valid description";
    } else if (newCycle.description.length > 500) {
      errors.description = "Description must be ≤ 500 characters";
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(newCycle.description)) {
      errors.description = "Description can only contain alphanumeric characters, spaces, and commas";
    } else if (newCycle.description.trim().length < 5) {
      errors.description = "Please enter a valid description (min 5 chars)";
    }
    if (!newCycle.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!newCycle.endDate) {
      errors.endDate = "End date is required";
    }
    if (newCycle.startDate && newCycle.endDate) {
      const s = new Date(newCycle.startDate);
      const eDate = new Date(newCycle.endDate);
      if (s > eDate) {
        errors.endDate = "End date must be after start date";
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!editingCycleId && s < today) {
        errors.startDate = "Start date cannot be in the past";
      }
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!newCycle.name?.trim() || !newCycle.description?.trim() || !newCycle.startDate || !newCycle.endDate) {
      toast.error('Please fill all required fields.');
      return;
    }

    try {
      const cycleData = {
        cycleName: newCycle.name,
        startDate: newCycle.startDate,
        endDate: newCycle.endDate,
        description: newCycle.description,
        status: newCycle.status || "draft",
        stages: editingCycleId && editingCycleData?.stages ? editingCycleData.stages : scheduleDefaultStages(newCycle.startDate, newCycle.endDate),
      };

      if (editingCycleId) {
        await updateCycle(editingCycleId, cycleData);
        toast.success("Performance cycle updated successfully!");
      } else {
        await createCycle(cycleData);
        toast.success("Performance cycle created successfully!");
      }

      handleCloseModal();
      refetch();
    } catch (error) {
      toast.error(
        `Failed to ${editingCycleId ? 'update' : 'create'} performance cycle: ${error.message || "Unknown error"}`
      );
    }
  };

  const handleViewCycle = (cycleId) => {
    navigate(`/performance_cycle_detail/${cycleId}`);
  };

  const handleEditCycle = (cycleId) => {
    setEditingCycleId(cycleId);
    setShowCreateModal(true);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('edit', cycleId);
      return params;
    }, { replace: true });
  };

  const handleDeleteCycle = (cycleId) => {
    const cycle = transformedCycles.find((cycle) => cycle.id === cycleId);
    setIsBulkDelete(false);
    setCycleToDelete(cycle);
    setShowDeleteWarning(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteWarning(true);
  };

  const confirmDeleteCycle = async () => {
    if (!cycleToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            const cycle = transformedCycles.find(c => c.id === id);
            if (cycle && (cycle.status === "draft" || cycle.status === "closed")) {
              await deleteCycle(id);
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} performance cycle(s) deleted successfully`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} cycle(s) could not be deleted (ensure they are in draft or closed status)`);
        }
      } else {
        await deleteCycle(cycleToDelete.id);
        toast.success("Performance cycle deleted successfully");
      }
      setShowDeleteWarning(false);
      setCycleToDelete(null);
      setIsBulkDelete(false);
      refetch();
    } catch (error) {
      toast.error("Failed to delete performance cycle");
    }
  };

  const cancelDeleteCycle = () => {
    setShowDeleteWarning(false);
    setCycleToDelete(null);
    setIsBulkDelete(false);
  };

  const filteredCycles = transformedCycles;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedCycles = useMemo(() => {
    if (!filteredCycles) return [];
    if (!sortField) return filteredCycles;

    return [...filteredCycles].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'year':
          aValue = a.year || 0;
          bValue = b.year || 0;
          if (aValue === bValue) {
            aValue = new Date(a.startDateRaw || 0).getTime();
            bValue = new Date(b.startDateRaw || 0).getTime();
          }
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'dueDate':
          aValue = new Date(a.dueDate || 0).getTime();
          bValue = new Date(b.dueDate || 0).getTime();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCycles, sortField, sortOrder]);

  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, filterYear, filterStartDate, filterEndDate, debouncedSearch]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(filteredCycles.map(cycle => cycle.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (cycle) => {
    const newSet = new Set(selectedRows);
    if (newSet.has(cycle.id)) {
      newSet.delete(cycle.id);
    } else {
      newSet.add(cycle.id);
    }
    setSelectedRows(newSet);
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = async (format) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const ids = Array.from(selectedRows);
    if (!ids.length) {
      toast.error('No selected cycles found to export.');
      return;
    }
    setExportLoading(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
    try {
      await requestBackendExport({
        scope: 'selected',
        format,
        selectedIds: ids,
      });
      toast.success(`${ids.length} cycle(s) exported!`, { id: toastId });
      setShowInlineSelectedFormats(false);
    } catch (error) {
      toast.error(error?.message || 'Export failed.', { id: toastId });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportScoped = React.useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        if (scope === 'currentPage' && !filteredCycles.length) {
          toast.error('No data on current page to export.', { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, createdAtStart: startDate, createdAtEnd: endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [filteredCycles, baseExportFilters, requestBackendExport]
  );

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Failed to load performance cycles
          </h3>
          <p className="text-red-700 mb-4">
            {error?.message || "An error occurred while loading data"}
          </p>
          <Button
            variant="primary"
            onClick={() => refetch()}
            className="bg-red-600 hover:bg-red-700"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isDrawer && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Performance Cycles
              </h1>
              <p className="text-gray-600">
                Manage performance review cycles and configurations
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {selectedRows.size > 0 && (
                <PermissionGate permission="performance" action="delete">
                  <Button
                    variant="primary"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete ({selectedRows.size})
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate permission="performance" action="read">
                <div className="relative">
                  <Button
                    variant="outline"
                    icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                    onClick={handleExportClick}
                    disabled={exportLoading}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ""}
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? "rotate-180" : ""}`} />
                  </Button>

                  <SelectedExportFormatBar
                    isOpen={showInlineSelectedFormats}
                    onClose={() => setShowInlineSelectedFormats(false)}
                    onExport={handleExportSelected}
                    loading={exportLoading}
                    className="absolute right-0 mt-2 z-50 min-w-[200px]"
                  />
                </div>
              </PermissionGate>
              <PermissionGate permission="performance" action="create">
                <Button
                  variant="outline"
                  icon={<Upload className="h-4 w-4" />}
                  onClick={() => setShowBulkUpload(true)}
                  className="border-agrosaf-green text-agrosaf-green hover:bg-agrosaf-green/10"
                >
                  Bulk Upload
                </Button>
              </PermissionGate>
              <PermissionGate permission="performance" action="create">
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowCreateModal(true)}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Create Cycle
                </Button>
              </PermissionGate>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-end gap-4 w-full">
              <div className="relative flex-[2] min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search cycles..."
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                    setSearchTerm(value);
                  }}
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  maxLength={100}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      const input = document.querySelector('input[placeholder="Search cycles..."]');
                      if (input) input.focus();
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex-shrink-0">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="launched">Launched</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="flex-shrink-0">
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                >
                  <option value="all">All Years</option>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    for (let i = 2; i >= 1; i--) {
                      years.push(currentYear + i);
                    }
                    years.push(currentYear);
                    for (let i = 1; i <= 3; i++) {
                      years.push(currentYear - i);
                    }
                    return years.map(year => (
                      <option key={year} value={year.toString()}>{year}</option>
                    ));
                  })()}
                </select>
              </div>
              <div className="flex-shrink-0">
                <DateRangeFilter
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  onChange={({ startDate, endDate }) => {
                    setFilterStartDate(startDate);
                    setFilterEndDate(endDate);
                  }}
                  inputClassName="w-32 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                />
              </div>
              {(searchTerm || filterStatus !== 'all' || filterYear !== 'all' || filterStartDate || filterEndDate) && (
                <div className="relative flex-shrink-0">
                  <Button
                    variant="outline"
                    icon={<X className="h-4 w-4" />}
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                      setFilterYear('all');
                      setFilterStartDate('');
                      setFilterEndDate('');
                    }}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showCreateModal && (
        isDrawer ? (
          <div className="bg-white">
            {editingCycleId && isLoadingEditingCycle ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
              </div>
            ) : (
              <form onSubmit={handleCreateCycle} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={newCycle.name}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 100);
                    setNewCycle({ ...newCycle, name: value });
                    if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                  }}
                  onBlur={handleBlur}
                  onKeyPress={handleTextKeyPress}
                  maxLength={100}
                  className={`mt-1 block w-full border rounded-lg p-2 ${touched.name && formErrors.name ? "border-red-500" : "border-gray-300"}`}
                  required
                />
                <div className="flex justify-between items-center mt-1">
                  {touched.name && formErrors.name && (
                    <p className="text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {newCycle.name.length}/100 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={newCycle.description}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                    setNewCycle({ ...newCycle, description: value });
                    if (formErrors.description) setFormErrors({ ...formErrors, description: undefined });
                  }}
                  onBlur={handleBlur}
                  onKeyPress={handleTextKeyPress}
                  maxLength={500}
                  className={`mt-1 block w-full border rounded-lg p-2 ${touched.description && formErrors.description ? "border-red-500" : "border-gray-300"}`}
                  required
                  rows={6}
                />
                <div className="flex justify-between items-center mt-1">
                  {touched.description && formErrors.description && (
                    <p className="text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {newCycle.description.length}/500 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="startDate"
                    value={newCycle.startDate}
                    onChange={(e) => {
                      setNewCycle({ ...newCycle, startDate: e.target.value });
                      if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: undefined });
                    }}
                    onBlur={handleBlur}
                    onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                    className={`mt-1 block w-full border rounded-lg p-2 ${touched.startDate && formErrors.startDate ? "border-red-500" : "border-gray-300"}`}
                    min={(new Date()).toISOString().split('T')[0]}
                    required
                  />
                </div>
                {touched.startDate && formErrors.startDate && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {formErrors.startDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  End Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="endDate"
                    value={newCycle.endDate}
                    onChange={(e) => {
                      setNewCycle({ ...newCycle, endDate: e.target.value });
                      if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: undefined });
                    }}
                    onBlur={handleBlur}
                    onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                    className={`mt-1 block w-full border rounded-lg p-2 ${touched.endDate && formErrors.endDate ? "border-red-500" : "border-gray-300"}`}
                    min={newCycle.startDate || (new Date()).toISOString().split('T')[0]}
                    required
                  />
                </div>
                {touched.endDate && formErrors.endDate && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {formErrors.endDate}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  value={newCycle.status}
                  onChange={(e) =>
                    setNewCycle({ ...newCycle, status: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="draft">Draft</option>
                  <option value="launched">Launched</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <PermissionGate permission="performance" action={editingCycleId ? "update" : "create"}>
                  <Button
                    type="submit"
                    variant="primary"
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  >
                    {editingCycleId ? "Save" : "Create"}
                  </Button>
                </PermissionGate>
              </div>
            </form>
            )}
          </div>
        ) : (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">{editingCycleId ? "Edit Performance Cycle" : "Create New Cycle"}</h2>
                <Button
                  variant="ghost"
                  icon={<X className="h-5 w-5" />}
                  onClick={handleCloseModal}
                />
              </div>
              {editingCycleId && isLoadingEditingCycle ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
                </div>
              ) : (
                <form onSubmit={handleCreateCycle} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newCycle.name}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 100);
                      setNewCycle({ ...newCycle, name: value });
                      if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                    }}
                    onBlur={handleBlur}
                    onKeyPress={handleTextKeyPress}
                    maxLength={100}
                    className={`mt-1 block w-full border rounded-lg p-2 ${touched.name && formErrors.name ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                  <div className="flex justify-between items-center mt-1">
                    {touched.name && formErrors.name && (
                      <p className="text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {formErrors.name}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {newCycle.name.length}/100 characters
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={newCycle.description}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                      setNewCycle({ ...newCycle, description: value });
                      if (formErrors.description) setFormErrors({ ...formErrors, description: undefined });
                    }}
                    onBlur={handleBlur}
                    onKeyPress={handleTextKeyPress}
                    maxLength={500}
                    className={`mt-1 block w-full border rounded-lg p-2 ${touched.description && formErrors.description ? "border-red-500" : "border-gray-300"}`}
                    required
                    rows={6}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {touched.description && formErrors.description && (
                      <p className="text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {formErrors.description}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {newCycle.description.length}/500 characters
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="startDate"
                      value={newCycle.startDate}
                      onChange={(e) => {
                        setNewCycle({ ...newCycle, startDate: e.target.value });
                        if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: undefined });
                      }}
                      onBlur={handleBlur}
                      onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                      className={`mt-1 block w-full border rounded-lg p-2 ${touched.startDate && formErrors.startDate ? "border-red-500" : "border-gray-300"}`}
                      min={(new Date()).toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  {touched.startDate && formErrors.startDate && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.startDate}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="endDate"
                      value={newCycle.endDate}
                      onChange={(e) => {
                        setNewCycle({ ...newCycle, endDate: e.target.value });
                        if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: undefined });
                      }}
                      onBlur={handleBlur}
                      onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                      className={`mt-1 block w-full border rounded-lg p-2 ${touched.endDate && formErrors.endDate ? "border-red-500" : "border-gray-300"}`}
                      min={newCycle.startDate || (new Date()).toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  {touched.endDate && formErrors.endDate && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.endDate}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={newCycle.status}
                    onChange={(e) =>
                      setNewCycle({ ...newCycle, status: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-lg p-2"
                  >
                    <option value="draft">Draft</option>
                    <option value="launched">Launched</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="performance" action={editingCycleId ? "update" : "create"}>
                    <Button
                      type="submit"
                      variant="primary"
                      className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                      disabled={false}
                    >
                      {editingCycleId ? "Save" : "Create"}
                    </Button>
                  </PermissionGate>
                </div>
              </form>
              )}
            </div>
          </div>
        )
      )}

      {!isDrawer && (
        <>
          {isLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading performance cycles...</p>
            </div>
          )}

          {!isLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 capitalize">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                          checked={filteredCycles.length > 0 && filteredCycles.every(c => selectedRows.has(c.id))}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Cycle Details</span>
                          {sortField === 'name' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                        onClick={() => handleSort('year')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Period</span>
                          {sortField === 'year' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortField === 'status' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                        onClick={() => handleSort('dueDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Due Date</span>
                          {sortField === 'dueDate' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created At</span>
                          {sortField === 'createdAt' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCycles.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <FileText className="h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No performance cycles found</h3>
                            {(searchTerm || filterStatus !== 'all' || filterYear !== 'all') && (
                              <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                            )}
                            {!searchTerm && filterStatus === 'all' && filterYear === 'all' && (
                              <p className="text-sm text-gray-500">Get started by creating your first performance cycle.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sortedCycles.map((cycle) => (
                        <tr key={cycle.id} className={`hover:bg-gray-50 ${selectedRows.has(cycle.id) ? 'bg-blue-50/50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                              checked={selectedRows.has(cycle.id)}
                              onChange={() => handleSelectRow(cycle)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div
                                className="text-sm font-medium text-gray-900"
                                title={cycle.name}
                              >
                                {cycle.name && cycle.name.length > 30
                                  ? `${cycle.name.slice(0, 30)}...`
                                  : cycle.name}
                              </div>
                              <div
                                className="text-sm text-gray-500"
                                title={cycle.description}
                              >
                                {cycle.description && cycle.description.length > 30
                                  ? `${cycle.description.slice(0, 30)}...`
                                  : cycle.description}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {cycle.period}
                            </div>
                            <div className="text-xs text-gray-500">
                              Year: {cycle.year}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                cycle.status
                              )}`}
                            >
                              {getStatusIcon(cycle.status)}
                              <span className="ml-1 capitalize">{cycle.status}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {cycle.dueDate ? formatDate(cycle.dueDate, { includeTime: false }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {cycle.createdAt ? formatDate(cycle.createdAt, { includeTime: true }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <PermissionGate permission="performance" action="read">
                                <button
                                  onClick={() => handleViewCycle(cycle.id)}
                                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="performance" action="update">
                                <button
                                  onClick={() => handleEditCycle(cycle.id)}
                                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              {(() => {
                                const isDeletable = cycle.status === "draft" || cycle.status === "closed";
                                return (
                                  <PermissionGate permission="performance" action="delete">
                                    <button
                                      onClick={isDeletable ? () => handleDeleteCycle(cycle.id) : undefined}
                                      disabled={!isDeletable}
                                      className={`p-2 rounded-lg transition-colors ${isDeletable
                                        ? "text-red-600 hover:text-red-800 hover:bg-red-50"
                                        : "text-gray-400 opacity-50 cursor-not-allowed"
                                        }`}
                                      title={
                                        isDeletable
                                          ? "Delete cycle"
                                          : "Cannot delete a cycle that is not in draft or closed status"
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={cancelDeleteCycle}
        onConfirm={confirmDeleteCycle}
        title={isBulkDelete ? "Delete Performance Cycles" : "Delete Performance Cycle"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedRows.size} selected performance cycle(s)? Only cycles in draft or closed status will be deleted. This action cannot be undone.`
          : `Are you sure you want to delete "${cycleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {showViewModal && selectedCycle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-start mb-4 gap-4">
              <div className="max-w-full">
                <h2 className="text-xl font-semibold break-words capitalize">
                  {selectedCycle.name}
                </h2>
                {selectedCycle.description && (
                  <p className="text-gray-600 text-sm mt-1 whitespace-pre-wrap break-words capitalize">
                    {selectedCycle.description}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs uppercase text-gray-500 mb-1">Period</p>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {getCyclePeriodDisplay(selectedCycle)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs uppercase text-gray-500 mb-1">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium \${getStatusColor(selectedCycle.status)}`}>
                  {getStatusIcon(selectedCycle.status)}
                  <span className="ml-1 capitalize">{selectedCycle.status}</span>
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs uppercase text-gray-500 mb-1">Due Date</p>
                <p className="text-gray-900">{selectedCycle.dueDate ? formatDate(selectedCycle.dueDate, { includeTime: false }) : 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs uppercase text-gray-500 mb-1">Year</p>
                <p className="text-gray-900">{selectedCycle.year}</p>
              </div>
            </div>

            <div className="flex justify-end mt-6 space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                icon={<Edit className="h-4 w-4" />}
                onClick={() => {
                  setShowViewModal(false);
                  handleEditCycle(selectedCycle.id);
                }}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Edit
              </Button>
            </div>
          </div>
        </div>
      )}

      {!isDrawer && paginationData && (
        <PaginationControls
          currentPage={page}
          totalPages={paginationData.totalPages}
          onPageChange={setPage}
          limit={limit}
          onLimitChange={setLimit}
          totalItems={paginationData.totalCycles}
        />
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Performance Cycles"
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: true,
        }}
      />
      {showBulkUpload && (
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => refetch()}
          config={{
            title: "Bulk Upload Performance Cycles",
            subtitle: "Import multiple performance cycles using a CSV file",
            templateEndpoint: "/performance/cycles/bulk-upload/template",
            validateEndpoint: "/performance/cycles/bulk-upload/validate",
            uploadEndpoint: "/performance/cycles/bulk-upload",
            templateFilename: "performance_cycles_bulk_upload_template.csv",
            dataKey: "data",
            maxRecords: 100,
            instructions: [
              "Cycle Name is required and must be unique.",
              "Start Date and End Date are required (YYYY-MM-DD).",
              "End Date must be after Start Date.",
              "Description is optional.",
              "Status can be: draft, launched, closed (default: draft)."
            ]
          }}
        />
      )}
    </div>
  );
};

export default PerformanceCycles;
